import dayjs from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";
import {
    bucketsToCountByDay,
    bucketsToCountByMonth,
    bucketsToCountByWeek,
    buildGameAggregatesFromStats,
    computeDayPopularityFromDayBuckets,
    reconcileDayCountsWithTableTotal,
    sumBucketValues,
} from "./dashboardAggregations";
import { selectedRangeChartTitle } from "./dashboardPeriods";

dayjs.extend(isoWeek);

/** Avoid Chart.js drawing a 1px border that looks like a tiny non-zero bar. */
function zeroSafeBorderWidth(fallback = 1) {
    return (ctx) => {
        const value = Number(ctx.dataset.data[ctx.dataIndex]);
        return value > 0 ? fallback : 0;
    };
}

function buildSelectedRangeSeries(subdivision, buckets, startDate, endDate, tableTotal) {
    if (subdivision === "week") {
        const byWeek = bucketsToCountByWeek(buckets, startDate, endDate);
        const keys = Object.keys(byWeek);
        return {
            labels: keys.map((key) => {
                const start = dayjs(key);
                const end = start.endOf("isoWeek");
                return `${start.format("D MMM")}–${end.format("D MMM")}`;
            }),
            counts: keys.map((key) => byWeek[key]),
        };
    }
    if (subdivision === "month") {
        const byMonth = bucketsToCountByMonth(buckets, startDate, endDate);
        const keys = Object.keys(byMonth);
        return {
            labels: keys.map((key) => dayjs(`${key}-01`).format("MMM YYYY")),
            counts: keys.map((key) => byMonth[key]),
        };
    }

    const byDay = reconcileDayCountsWithTableTotal(
        bucketsToCountByDay(buckets, startDate, endDate),
        tableTotal,
    );
    const keys = Object.keys(byDay);
    return {
        labels: keys.map((key) => dayjs(key).format("D MMM")),
        counts: keys.map((key) => byDay[key]),
    };
}

/**
 * Build Chart.js datasets and labels from GET /bookings/stats bucket data.
 */
export function buildDashboardChartViewModel({
    colors,
    baseFilters,
    periods,
    statsData,
    experienceNameById,
    weekdayLabels,
}) {
    const {
        startThisWeek,
        endThisWeek,
        startPrevWeek,
        endPrevWeek,
        startPrev2Week,
        endPrev2Week,
        startWeekLastYear,
        endWeekLastYear,
        previousMonthLabel,
        monthBeforeLabel,
        sameMonthLastYearLabel,
    } = periods;

    const {
        selectedRange,
        selectedRangeSubdivision = "day",
        periodBookingsByDay,
        filterStats,
    } = statsData;

    const weeklyKPI = {
        thisWeek: sumBucketValues(periodBookingsByDay.thisWeek),
        previousWeek: sumBucketValues(periodBookingsByDay.prevWeek),
        sameWeekLastYear: sumBucketValues(periodBookingsByDay.weekLastYear),
    };
    // Completed calendar months only (no current MTD), same idea as the 12-month report.
    const monthlyKPI = {
        previousMonth: sumBucketValues(periodBookingsByDay.prevMonth),
        monthBefore: sumBucketValues(periodBookingsByDay.monthBefore),
        sameMonthLastYear: sumBucketValues(periodBookingsByDay.sameMonthLastYear),
        previousMonthLabel,
        monthBeforeLabel,
        sameMonthLastYearLabel,
    };
    const yearToDateKPI = {
        current: sumBucketValues(periodBookingsByDay.ytd),
        previous: sumBucketValues(periodBookingsByDay.ytdPrev),
    };

    const selectedRangeSeries = buildSelectedRangeSeries(
        selectedRangeSubdivision,
        selectedRange.bookings ?? selectedRange.bookingsByDay,
        dayjs(baseFilters.startDate),
        dayjs(baseFilters.endDate),
        filterStats?.count,
    );
    const thisWeekByDay = bucketsToCountByDay(
        periodBookingsByDay.thisWeek,
        startThisWeek,
        endThisWeek,
    );
    const thisWeekLastYearByDay = bucketsToCountByDay(
        periodBookingsByDay.weekLastYear,
        startWeekLastYear,
        endWeekLastYear,
    );
    const prevWeekByDay = bucketsToCountByDay(
        periodBookingsByDay.prevWeek,
        startPrevWeek,
        endPrevWeek,
    );
    const prev2WeekByDay = bucketsToCountByDay(
        periodBookingsByDay.prev2Week,
        startPrev2Week,
        endPrev2Week,
    );
    const lastYearWeekByDay = bucketsToCountByDay(
        periodBookingsByDay.weekLastYear,
        startWeekLastYear,
        endWeekLastYear,
    );

    const dayKeysThisWeek = Object.keys(thisWeekByDay);
    const dayKeysPrevWeek = Object.keys(prevWeekByDay);
    const dayKeysPrev2Week = Object.keys(prev2WeekByDay);
    const dayKeysLastYear = Object.keys(lastYearWeekByDay);

    const selectedRangeDatasets = [
        {
            label: "Bookings",
            data: selectedRangeSeries.counts,
            backgroundColor: colors.seriesPrimaryFill,
            borderColor: colors.seriesPrimaryStroke,
            borderWidth: zeroSafeBorderWidth(1),
            borderRadius: 4,
        },
    ];

    const thisWeekDatasets = [
        {
            label: "This week",
            data: dayKeysThisWeek.map((key) => thisWeekByDay[key]),
            backgroundColor: colors.seriesPrimaryFill,
            borderColor: colors.seriesPrimaryStroke,
            borderWidth: zeroSafeBorderWidth(1),
            borderRadius: 4,
        },
        {
            label: "Same weekdays last year",
            data: Object.keys(thisWeekLastYearByDay).map((key) => thisWeekLastYearByDay[key]),
            backgroundColor: colors.seriesTertiarySoft,
            borderWidth: 0,
        },
    ];

    const previousWeeksDatasets = [
        {
            label: "Previous week",
            data: dayKeysPrevWeek.map((key) => prevWeekByDay[key]),
            backgroundColor: colors.seriesPrimaryFill,
            borderWidth: 0,
        },
        {
            label: "Week before",
            data: dayKeysPrev2Week.map((key) => prev2WeekByDay[key]),
            backgroundColor: colors.seriesSecondaryFill,
            borderWidth: 0,
        },
        {
            label: "Same week last year",
            data: dayKeysLastYear.map((key) => lastYearWeekByDay[key]),
            backgroundColor: colors.seriesTertiaryFill,
            borderWidth: 0,
        },
    ];

    const monthComparisonLabels = [previousMonthLabel, monthBeforeLabel, sameMonthLastYearLabel];
    const monthComparisonDatasets = [
        {
            label: "Bookings",
            data: [monthlyKPI.previousMonth, monthlyKPI.monthBefore, monthlyKPI.sameMonthLastYear],
            backgroundColor: [
                colors.seriesPrimaryFill,
                colors.seriesSecondaryFill,
                colors.seriesTertiaryFill,
            ],
            borderWidth: 0,
        },
    ];

    const { rankedByCount } = buildGameAggregatesFromStats(
        selectedRange.playByExperience,
        experienceNameById,
    );
    const dayPop = computeDayPopularityFromDayBuckets(
        selectedRange.bookingsByDay,
        selectedRange.playersByDay,
        weekdayLabels,
    );

    const popularDayLabels = dayPop.weekdayLabels;
    const popularDayDatasets = [
        {
            label: "Bookings",
            data: popularDayLabels.map((label) => dayPop.bookingCounts[label] || 0),
            backgroundColor: colors.seriesSecondaryFill,
            borderWidth: 0,
        },
        {
            label: "Players",
            data: popularDayLabels.map((label) => dayPop.playerTotals[label] || 0),
            backgroundColor: colors.seriesTertiaryFill,
            borderWidth: 0,
        },
    ];

    return {
        weeklyKPI,
        monthlyKPI,
        yearToDateKPI,
        selectedRangeChartTitle: selectedRangeChartTitle(selectedRangeSubdivision),
        selectedRangeLabels: selectedRangeSeries.labels,
        selectedRangeDatasets,
        thisWeekDatasets,
        previousWeeksDatasets,
        monthComparisonLabels,
        monthComparisonDatasets,
        topGames: rankedByCount.slice(0, 10),
        popularDayLabels,
        popularDayDatasets,
    };
}
